export const BASE_URL = window ? window.location.origin : 'http://localhost:3000';

export const BSC_CHAIN_ID = 56;

/** Etherscan API v2 key (`apikey` query param). */
export const ETHERSCAN_API_KEY = 'BV2ISWQ7GF881B4CYIRQW77WXGNUJ7F1RV';

/** Etherscan v2 REST base URL. */
export const ETHERSCAN_V2_API_BASE = 'https://api.etherscan.io/v2/api';

/** BSC mainnet chain id for `getLogs` queries used by analytics + player APIs. */
export const ETHERSCAN_BSC_MAINNET_CHAIN_ID = 56;

/** Inclusive `fromBlock` for game / vault log pagination on BSC mainnet. */
export const ETHERSCAN_GAME_LOGS_FROM_BLOCK = 98830223;

/** Game contract `Bought` event topic0. */
export const BOUGHT_EVENT_TOPIC0 =
  '0xe93116bd7ff763820a64baa0c3ab15bc56a3e0eb25041dcfd00e15fd2fe8294c';

/** Game contract `Winner` event topic0. */
export const WINNER_EVENT_TOPIC0 =
  '0x965cb8ca49cdae499b03bce916f28ef0062fa8900781378dc12642c2a3e49aba';

/** Core vault near-win / payout event topic0 (player win history). */
export const VAULT_WINNER_EVENT_TOPIC0 =
  '0x3a9df07c74fc532a30d5bced4dd7a4c3e1c2766d9751556a0dec8662702f6f7e';

/** Vault log topic0 used in global stats seeding. */
export const VAULT_STATS_EVENT_TOPIC0 =
  '0x6d6e7e316526bc552938a42155f3775c5ca55d97b9f1f4fbf620fda87720f608';

/** Core vault referral level report event topic0 (dashboard level report). */
export const VAULT_REFERRAL_LEVEL_EVENT_TOPIC0 =
  '0x8afccaf365f6ae733b0d2c66eaf0924093b8665eaaab43f6f232acce7ebf0f5b';

/** Minimum milliseconds to wait after each Etherscan `getLogs` request finishes. */
export const ETHERSCAN_GET_LOGS_REQUEST_DELAY_MS = 1500;

/** Identifies which app surface initiated an Etherscan `getLogs` request. */
export const ETHERSCAN_GET_LOGS_SOURCE = {
  ANALYTICS: 'analytics',
  HISTORY: 'history',
} as const;

export type EtherscanGetLogsSource =
  (typeof ETHERSCAN_GET_LOGS_SOURCE)[keyof typeof ETHERSCAN_GET_LOGS_SOURCE];

/** Per-page defaults for Etherscan log pagination and throttling. */
export interface EtherscanGetLogsSourceProfile {
  label: string;
  pageOffset: number;
  defaultFromBlock: number;
  requestDelayMs: number;
}

export const ETHERSCAN_GET_LOGS_SOURCE_PROFILES: Record<
  EtherscanGetLogsSource,
  EtherscanGetLogsSourceProfile
> = {
  [ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS]: {
    label: 'Analytics',
    pageOffset: 1000,
    defaultFromBlock: ETHERSCAN_GAME_LOGS_FROM_BLOCK,
    requestDelayMs: ETHERSCAN_GET_LOGS_REQUEST_DELAY_MS,
  },
  [ETHERSCAN_GET_LOGS_SOURCE.HISTORY]: {
    label: 'History',
    pageOffset: 1000,
    defaultFromBlock: ETHERSCAN_GAME_LOGS_FROM_BLOCK,
    requestDelayMs: ETHERSCAN_GET_LOGS_REQUEST_DELAY_MS,
  },
};

export interface EtherscanBscMainNetGetLogsOptions {
  address: string;
  topic0: string;
  page?: number;
  offset?: number;
  fromBlock?: number;
  toBlock?: number | 'latest';
  /** Analytics Last Draw vs player History — selects profile defaults and queue. */
  source?: EtherscanGetLogsSource;
  /** Connected wallet — filters indexed address topic (History page). */
  walletAddress?: string;
  /** Topic slot for `walletAddress` (`1` = vault winner, `2` = Bought buyer). */
  walletTopicIndex?: 1 | 2;
}

/** Indexed address topic filter for Etherscan `getLogs` (`topic1` / `topic2`). */
export function encodeIndexedAddressTopic(address: string): string {
  const raw = String(address || '')
    .toLowerCase()
    .replace(/^0x/, '');
  if (!/^[0-9a-f]{40}$/.test(raw)) return '';
  return `0x${'0'.repeat(24)}${raw}`;
}

export const ETHERSCAN_WALLET_TOPIC_INDEX = {
  VAULT_WINNER: 1,
  BOUGHT_BUYER: 2,
} as const;

export interface ResolvedEtherscanBscMainNetGetLogsOptions extends EtherscanBscMainNetGetLogsOptions {
  page: number;
  offset: number;
  resolvedFromBlock: number;
  resolvedToBlock: number | 'latest';
  requestDelayMs: number;
  sourceLabel: string;
  walletTopicFilter?: string;
}

/** Merge explicit options with the profile for `source` (Analytics / History). */
export function resolveEtherscanGetLogsOptions(
  options: EtherscanBscMainNetGetLogsOptions,
): ResolvedEtherscanBscMainNetGetLogsOptions {
  const source = options.source ?? ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS;
  const profile = ETHERSCAN_GET_LOGS_SOURCE_PROFILES[source];
  const walletTopicFilter = options.walletAddress
    ? encodeIndexedAddressTopic(options.walletAddress)
    : undefined;
  return {
    ...options,
    source,
    page: options.page ?? 0,
    offset: options.offset ?? profile.pageOffset,
    resolvedFromBlock: options.fromBlock ?? profile.defaultFromBlock,
    resolvedToBlock: options.toBlock ?? 'latest',
    requestDelayMs: profile.requestDelayMs,
    sourceLabel: profile.label,
    walletTopicFilter: walletTopicFilter || undefined,
  };
}

const ETHERSCAN_GET_LOGS_MAX_ATTEMPTS = 6;
const ETHERSCAN_GET_LOGS_BUSY_BASE_MS = 800;

type EtherscanGetLogsQueueKey = EtherscanGetLogsSource | 'default';

const etherscanGetLogsChains: Record<EtherscanGetLogsQueueKey, Promise<void>> = {
  default: Promise.resolve(),
  analytics: Promise.resolve(),
  history: Promise.resolve(),
};

const lastEtherscanGetLogsRequestAtBySource: Partial<Record<EtherscanGetLogsQueueKey, number>> =
  {};

function getEtherscanGetLogsQueueKey(source?: EtherscanGetLogsSource): EtherscanGetLogsQueueKey {
  return source ?? 'default';
}

function etherscanGetLogsSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getEtherscanResponseCombinedText(json: unknown): string {
  if (!json || typeof json !== 'object') return '';
  const record = json as { message?: string; result?: unknown };
  const msg = String(record.message ?? '').toLowerCase();
  const resultStr = typeof record.result === 'string' ? record.result.toLowerCase() : '';
  return `${msg} ${resultStr}`.trim();
}

/** Etherscan returns this when a `getLogs` query has zero matches — not an error. */
export function isEtherscanNoRecordsFoundResponse(json: unknown): boolean {
  return /no records found/i.test(getEtherscanResponseCombinedText(json));
}

function isEtherscanBusyResponse(json: unknown): boolean {
  if (!json || typeof json !== 'object') return false;
  if (isEtherscanNoRecordsFoundResponse(json)) return false;
  const combined = getEtherscanResponseCombinedText(json);
  return /server busy|rate limit|max calls|too many|timeout|try again|temporarily|unexpected|notok/i.test(
    combined,
  );
}

function enqueueEtherscanGetLogsTask<T>(
  source: EtherscanGetLogsSource | undefined,
  task: () => Promise<T>,
): Promise<T> {
  const queueKey = getEtherscanGetLogsQueueKey(source);
  const chain = etherscanGetLogsChains[queueKey];
  const next = chain.then(task, task);
  etherscanGetLogsChains[queueKey] = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

async function awaitEtherscanGetLogsSlot(requestDelayMs: number, source?: EtherscanGetLogsSource): Promise<void> {
  const queueKey = getEtherscanGetLogsQueueKey(source);
  const now = Date.now();
  const lastAt = lastEtherscanGetLogsRequestAtBySource[queueKey] ?? 0;
  const waitMs = lastAt ? Math.max(0, requestDelayMs - (now - lastAt)) : 0;
  if (waitMs > 0) {
    await etherscanGetLogsSleep(waitMs);
  }
}

function markEtherscanGetLogsRequestComplete(source?: EtherscanGetLogsSource): void {
  const queueKey = getEtherscanGetLogsQueueKey(source);
  lastEtherscanGetLogsRequestAtBySource[queueKey] = Date.now();
}

function buildEtherscanBscMainNetGetLogsUrlString(
  options: EtherscanBscMainNetGetLogsOptions,
): string {
  const resolved = resolveEtherscanGetLogsOptions(options);
  let url = `${ETHERSCAN_V2_API_BASE}?chainid=${ETHERSCAN_BSC_MAINNET_CHAIN_ID}&module=logs&action=getLogs&address=${resolved.address}&fromBlock=${resolved.resolvedFromBlock}&toBlock=${resolved.resolvedToBlock}&topic0=${resolved.topic0}&page=${resolved.page}&offset=${resolved.offset}&apikey=${ETHERSCAN_API_KEY}`;
  if (resolved.walletTopicFilter && resolved.walletTopicIndex === 1) {
    url += `&topic1=${resolved.walletTopicFilter}`;
  } else if (resolved.walletTopicFilter && resolved.walletTopicIndex === 2) {
    url += `&topic2=${resolved.walletTopicFilter}`;
  }
  return url;
}

/**
 * Build Etherscan v2 `getLogs` URL for BSC mainnet. Queued per `source` so Analytics
 * and History requests do not block each other's throttle slots.
 */
export async function buildEtherscanBscMainNetGetLogsUrl(
  options: EtherscanBscMainNetGetLogsOptions,
): Promise<string> {
  const resolved = resolveEtherscanGetLogsOptions(options);
  return enqueueEtherscanGetLogsTask(resolved.source, async () => {
    await awaitEtherscanGetLogsSlot(resolved.requestDelayMs, resolved.source);
    const url = buildEtherscanBscMainNetGetLogsUrlString(options);
    markEtherscanGetLogsRequestComplete(resolved.source);
    return url;
  });
}

/**
 * Fetch Etherscan `getLogs` for BSC mainnet. Requests are queued per `source`; after each
 * HTTP call finishes the queue waits `requestDelayMs` (1s by default) before the next call.
 */
export async function fetchEtherscanBscMainNetGetLogs(
  options: EtherscanBscMainNetGetLogsOptions,
): Promise<unknown> {
  const resolved = resolveEtherscanGetLogsOptions(options);
  return enqueueEtherscanGetLogsTask(resolved.source, async () => {
    try {
      let lastJson: unknown = null;
      for (let attempt = 0; attempt < ETHERSCAN_GET_LOGS_MAX_ATTEMPTS; attempt += 1) {
        const url = buildEtherscanBscMainNetGetLogsUrlString(options);
        try {
          const res = await fetch(url, { method: 'GET' });
          const json = await res.json();
          if (isEtherscanNoRecordsFoundResponse(json)) {
            return { status: '1', message: 'No records found', result: [], noRecordsFound: true };
          }
          if (isEtherscanBusyResponse(json)) {
            lastJson = json;
            if (attempt < ETHERSCAN_GET_LOGS_MAX_ATTEMPTS - 1) {
              await etherscanGetLogsSleep(ETHERSCAN_GET_LOGS_BUSY_BASE_MS * 2 ** attempt);
            }
            continue;
          }
          return json;
        } catch (e) {
          if (attempt === ETHERSCAN_GET_LOGS_MAX_ATTEMPTS - 1) throw e;
          await etherscanGetLogsSleep(ETHERSCAN_GET_LOGS_BUSY_BASE_MS * 2 ** attempt);
        }
      }

      return lastJson;
    } finally {
      await etherscanGetLogsSleep(resolved.requestDelayMs);
    }
  });
}

/** Resolve a BSC mainnet block number from a unix timestamp (seconds). */
export async function fetchEtherscanBscBlockByTimestamp(
  timestampSec: number,
  closest: 'before' | 'after' = 'before',
): Promise<number> {
  const ts = String(Math.floor(timestampSec));
  const url = `${ETHERSCAN_V2_API_BASE}?chainid=${ETHERSCAN_BSC_MAINNET_CHAIN_ID}&module=block&action=getblocknobytime&timestamp=${ts}&closest=${closest}&apikey=${ETHERSCAN_API_KEY}`;

  let lastError: unknown = null;
  for (let attempt = 0; attempt < ETHERSCAN_GET_LOGS_MAX_ATTEMPTS; attempt += 1) {
    try {
      const res = await fetch(url, { method: 'GET' });
      const json = await res.json();
      if (isEtherscanBusyResponse(json)) {
        lastError = json;
        await etherscanGetLogsSleep(ETHERSCAN_GET_LOGS_BUSY_BASE_MS * 2 ** attempt);
        continue;
      }
      const block = Number((json as { result?: unknown }).result);
      if (Number.isFinite(block) && block > 0) return block;
      lastError = json;
    } catch (e) {
      lastError = e;
      if (attempt === ETHERSCAN_GET_LOGS_MAX_ATTEMPTS - 1) throw e;
      await etherscanGetLogsSleep(ETHERSCAN_GET_LOGS_BUSY_BASE_MS * 2 ** attempt);
    }
  }

  throw new Error(
    `Failed to resolve block for timestamp ${ts}: ${String((lastError as { message?: string })?.message ?? lastError)}`,
  );
}

// ---------------------------------------------------------------------------
// Date range filters (Analytics Last Draw, History, Etherscan block resolution)
// ---------------------------------------------------------------------------

/** Default lookback for date-range filters: last 24 hours through now. */
export const DEFAULT_DATE_RANGE_LOOKBACK_MS = 24 * 60 * 60 * 1000;

export interface DateRangeFilter {
  dateFrom?: string;
  dateTo?: string;
  fromTimestampSec?: number;
  toTimestampSec?: number;
}

export interface BlockRange {
  fromBlock?: number;
  toBlock?: number | 'latest';
}

/** Format a local calendar date as `YYYY-MM-DD`. */
export function toLocalISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse `YYYY-MM-DD` into a local-midnight `Date`, or `null` if invalid. */
export function parseLocalISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Format a `Date` as `YYYY-MM-DD` for pickers and filters. */
export function formatLocalISODate(date: Date | null | undefined): string {
  if (!date) return '';
  return toLocalISODate(date);
}

/** Human-readable label for an applied date range. */
export function formatAppliedDateRangeLabel(dateRange?: DateRangeFilter | null): string | null {
  if (!dateRange) return null;
  const { dateFrom, dateTo } = dateRange;
  if (dateFrom && dateTo) return `${dateFrom} → ${dateTo}`;
  if (dateFrom) return `from ${dateFrom}`;
  if (dateTo) return `through ${dateTo}`;
  return null;
}

/** Default filter: from 24 hours ago through now (exact timestamps included). */
export function getDefaultDateRange(): DateRangeFilter {
  const nowMs = Date.now();
  const fromMs = nowMs - DEFAULT_DATE_RANGE_LOOKBACK_MS;
  return {
    dateFrom: toLocalISODate(new Date(fromMs)),
    dateTo: toLocalISODate(new Date(nowMs)),
    fromTimestampSec: Math.floor(fromMs / 1000),
    toTimestampSec: Math.floor(nowMs / 1000),
  };
}

/** @deprecated Alias for {@link getDefaultDateRange}. */
export function getDefaultLastDrawDateRange(): DateRangeFilter {
  return getDefaultDateRange();
}

export function hasDateRangeFilter(dateRange?: DateRangeFilter | null): boolean {
  if (!dateRange) return false;
  return Boolean(
    dateRange.dateFrom ||
    dateRange.dateTo ||
    dateRange.fromTimestampSec != null ||
    dateRange.toTimestampSec != null,
  );
}

/** Inclusive start timestamp (ms) for client-side bet/log filtering. */
export function parseFilterTimestampFrom(filters: DateRangeFilter): number | null {
  if (filters.fromTimestampSec != null) return filters.fromTimestampSec * 1000;
  if (!filters.dateFrom) return null;
  return new Date(`${filters.dateFrom}T00:00:00`).getTime();
}

/** Inclusive end timestamp (ms) for client-side bet/log filtering. */
export function parseFilterTimestampTo(filters: DateRangeFilter): number | null {
  if (filters.toTimestampSec != null) return filters.toTimestampSec * 1000;
  if (!filters.dateTo) return null;
  const todayISO = toLocalISODate(new Date());
  if (filters.dateTo === todayISO) return Date.now();
  return new Date(`${filters.dateTo}T23:59:59.999`).getTime();
}

/** Merge arbitrary table filters with a date range (History, etc.). */
export function mergeFiltersWithDateRange<T extends Record<string, unknown>>(
  baseFilters: T,
  dateRange: DateRangeFilter,
): T & DateRangeFilter {
  return {
    ...baseFilters,
    dateFrom: dateRange.dateFrom,
    dateTo: dateRange.dateTo,
    fromTimestampSec: dateRange.fromTimestampSec,
    toTimestampSec: dateRange.toTimestampSec,
  };
}

/** Convert `YYYY-MM-DD` dates to inclusive BSC block range via Etherscan `getblocknobytime`. */
const BLOCK_RANGE_CACHE_TTL_MS = 5 * 60 * 1000;
const blockRangeMemoryCache = new Map<string, { range: BlockRange; fetchedAt: number }>();

function blockRangeCacheKey(dateRange: DateRangeFilter): string {
  return JSON.stringify({
    dateFrom: dateRange.dateFrom ?? '',
    dateTo: dateRange.dateTo ?? '',
    fromTimestampSec: dateRange.fromTimestampSec ?? null,
    toTimestampSec: dateRange.toTimestampSec ?? null,
  });
}

export async function resolveBlockRangeFromDates(dateRange: DateRangeFilter = {}): Promise<BlockRange> {
  const { dateFrom, dateTo, fromTimestampSec, toTimestampSec } = dateRange;
  if (!hasDateRangeFilter(dateRange)) {
    return { fromBlock: undefined, toBlock: undefined };
  }

  const cacheKey = blockRangeCacheKey(dateRange);
  const cached = blockRangeMemoryCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < BLOCK_RANGE_CACHE_TTL_MS) {
    return cached.range;
  }

  const todayISO = toLocalISODate(new Date());
  const startTs =
    fromTimestampSec != null
      ? fromTimestampSec
      : dateFrom
        ? Math.floor(new Date(`${dateFrom}T00:00:00`).getTime() / 1000)
        : null;
  const endTs =
    toTimestampSec != null
      ? toTimestampSec
      : dateTo
        ? dateTo === todayISO
          ? Math.floor((Date.now() - 3000) / 1000)
          : Math.floor(new Date(`${dateTo}T23:59:59`).getTime() / 1000)
        : null;

  const [fromBlock, toBlock] = await Promise.all([
    startTs != null
      ? fetchEtherscanBscBlockByTimestamp(startTs, 'before')
      : Promise.resolve(ETHERSCAN_GAME_LOGS_FROM_BLOCK),
    endTs != null
      ? fetchEtherscanBscBlockByTimestamp(endTs, 'after')
      : Promise.resolve('latest' as const),
  ]);
  const range = { fromBlock, toBlock };
  blockRangeMemoryCache.set(cacheKey, { range, fetchedAt: Date.now() });
  return range;
}

/** Backend: notify winner generation when a game round sells out (Games page). */
export const GENERATE_WINNER_API_URL = 'https://api.hashmedora.com/generateWinner';

/** Backend: record a successful ticket purchase (Games page). */
export const ADD_BOUGHT_DATA_API_URL = 'https://api.hashmedora.com/addBoughtData';

/** Backend: winner history for Analytics “Last Draw winner and number” table. */
export const GET_WINNER_DATA_API_URL = 'https://api.hashmedora.com/getWinnerData';

/** Backend: ticket purchase history for Analytics global stats. */
export const GET_BOUGHT_DATA_API_URL = 'https://api.hashmedora.com/getBoughtData';
